import { Spinner } from "@repo/ui/components/Base/Spinner";
import { DataTable } from "@repo/ui/components/Base/DataTable";
import { layoutDefaults, useColumns } from "./AgendaTemplateTableColumns";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { PreviewAgendaTemplateDialog } from "./PreviewAgendaTemplateDialog";
import { ArchiveConfirmation } from "../dialog/ArchiveConfirmation";
import { useUserColumns } from "@/hooks/useUserColumns";
import { KEYS } from "@/lib/constants";
import { ReoccurringTemplateMeetingsDialog } from "./ReoccurringTemplateMeetingsDialog";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { AgendaTemplateCopyDialog } from "./AgendaTemplateCopyDialog";
import { agendaRepo } from "@/repositories/agendaRepo";
import { useAgendaTemplateList } from "@/hooks/queryHooks/agendaHooks";
import { useActiveTeamIdOrOrg } from "@/hooks/useActiveTeam";
import { meetingRepo } from "@/repositories/meetingRepo";

export const AgendaTemplateTable = ({ tenantId }: { tenantId: string }) => {
  const { data } = useAgendaTemplateList(
    tenantId,
    useActiveTeamIdOrOrg() ?? ""
  );
  const navigate = useNavigate();

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [reoccurringOpen, setReoccurringOpen] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [isCopyFormOpen, setIsCopyFormOpen] = useState(false);

  const template = useMemo(
    () => data?.find((d) => d.id === activeId),
    [data, activeId]
  );

  const { defaultState, saveTableLayout, resetTableLayout } = useUserColumns(
    KEYS.AGENDATEMPLATE_TABLE_COLUMNS,
    layoutDefaults
  );
  const handleEdit = (id: string) => {
    navigate(new AppUrlPath(tenantId).getAgendaTemplatePath(id));
  };

  const handleReoccurring = (id: string) => {
    setReoccurringOpen(true);
    setActiveId(id);
  };

  const handleCopy = (id: string) => {
    setIsCopyFormOpen(true);
    setActiveId(id);
  };

  const handlePreview = (id: string) => {
    setActiveId(id);
    setIsPreviewOpen(true);
  };

  const handleArchive = (docId: string) => {
    setActiveId(docId);
    setArchiveOpen(true);
  };

  return (
    <>
      {activeId && (
        <ArchiveConfirmation
          open={archiveOpen}
          setOpen={setArchiveOpen}
          itemId={activeId}
          onSubmit={async (agendaId) => {
            await agendaRepo.delete(tenantId, agendaId);
          }}
        />
      )}

      {template && (
        <ReoccurringTemplateMeetingsDialog
          open={reoccurringOpen}
          setOpen={setReoccurringOpen}
          template={template}
          tenantId={tenantId}
          templates={data}
        />
      )}

      {template && (
        <AgendaTemplateCopyDialog
          open={isCopyFormOpen}
          setOpen={setIsCopyFormOpen}
          template={template}
          tenantId={tenantId}
        />
      )}

      <PreviewAgendaTemplateDialog
        open={isPreviewOpen}
        setOpen={setIsPreviewOpen}
        meeting={meetingRepo.init({
          name: "{Meeting Title}",
          agenda: data?.find((item) => item.id === activeId) ?? null,
        })}
        letterHead={
          data?.find((item) => item.id === activeId)?.letterHead ?? undefined
        }
      />
      {!data && <Spinner />}

      <DataTable
        columns={useColumns({
          tenantId,
          handleEdit,
          handleReoccurring,
          handleCopy,
          handleView: handlePreview,
          handleArchive,
        })}
        includeMainFilter={false}
        data={data ?? []}
        userDefaults={defaultState}
        factoryDefaults={layoutDefaults}
        onLayoutSave={saveTableLayout}
        onLayoutReset={resetTableLayout}
      />
    </>
  );
};
